/**
 * The Studio chrome every public-form screen repeats: the white pill with the accent arrow, the
 * round back button, the logo and name, the avatar, the keycap, the MoveBuddy mark and the grain.
 *
 * One file because they are one concern — the template's recurring furniture — and each is drawn on
 * the welcome, the verification, the questions and the confirmation alike. Sizes switch on the
 * nearest container's width (`StudioParts.module.css`), so a part drawn inside a phone-sized frame is
 * the phone's part.
 */

import { useId, type ReactNode } from 'react';
import { clsx } from 'clsx';

import brandMark from '~/shared/ui/brandMark.module.css';

import { monogram } from '../model/landing';
import { StudioArrowGlyph, StudioBackGlyph } from './StudioGlyphs';
import styles from './StudioParts.module.css';

interface StudioPillProps {
  readonly label: string;
  /** Absent: the pill is drawn but is not a control — the builder's preview. */
  readonly onPress?: () => void;
  readonly type?: 'button' | 'submit';
  /**
   * The press is refused, and the reason is on screen beside it. The pill stays FOCUSABLE — a
   * disabled control drops out of the tab order and takes its explanation with it.
   */
  readonly refused?: boolean;
  /** A write is in flight; the pill says so to assistive technology and ignores presses. */
  readonly busy?: boolean;
  readonly describedBy?: string;
  readonly className?: string;
}

export function StudioPill({
  label,
  onPress,
  type = 'button',
  refused = false,
  busy = false,
  describedBy,
  className,
}: StudioPillProps) {
  const body = (
    <>
      <span className={styles.pillLabel}>{label}</span>
      <span className={styles.pillArrow} aria-hidden="true">
        <StudioArrowGlyph className={styles.pillGlyph} />
      </span>
    </>
  );
  if (onPress === undefined && type === 'button') {
    return <span className={clsx(styles.pill, className)}>{body}</span>;
  }
  return (
    <button
      type={type}
      className={clsx(styles.pill, styles.pillControl, className)}
      aria-disabled={refused || busy || undefined}
      aria-busy={busy || undefined}
      aria-describedby={describedBy}
      onClick={(event) => {
        if (refused || busy) {
          event.preventDefault();
          return;
        }
        onPress?.();
      }}
    >
      {body}
    </button>
  );
}

export function StudioBackButton({
  label,
  onPress,
  size = 'large',
  className,
}: {
  readonly label: string;
  readonly onPress: () => void;
  /** `large` beside a pill; `small` in a header. */
  readonly size?: 'large' | 'small';
  readonly className?: string;
}) {
  return (
    <button
      type="button"
      className={clsx(styles.back, size === 'small' && styles.backSmall, className)}
      aria-label={label}
      onClick={onPress}
    >
      <StudioBackGlyph className={styles.backGlyph} />
    </button>
  );
}

/** The trainer's logo — their own image, or their initials on a white tile — and their name. */
export function StudioBrandLine({
  displayName,
  logoUrl,
  showLogo,
  size = 'small',
  className,
}: {
  readonly displayName: string;
  readonly logoUrl: string | null;
  readonly showLogo: boolean;
  /** `large` on the welcome and the confirmation; `small` over a form. */
  readonly size?: 'large' | 'small';
  readonly className?: string;
}) {
  return (
    <div className={clsx(styles.brand, size === 'large' && styles.brandLarge, className)}>
      {showLogo &&
        (logoUrl === null ? (
          <span className={styles.logo} aria-hidden="true">
            {monogram(displayName)}
          </span>
        ) : (
          <img className={styles.logoImage} src={logoUrl} alt="" />
        ))}
      <span className={styles.brandName}>{displayName}</span>
    </div>
  );
}

/** A round photograph of the trainer, or their initials when there is none. */
export function StudioAvatar({
  displayName,
  url,
  className,
}: {
  readonly displayName: string;
  readonly url: string | null;
  readonly className?: string;
}) {
  if (url === null) {
    return (
      <span className={clsx(styles.avatarInitials, className)} aria-hidden="true">
        {monogram(displayName)}
      </span>
    );
  }
  return <img className={clsx(styles.avatar, className)} src={url} alt="" />;
}

/** A key the filler can press, drawn as a keycap. Decorative: the sentence around it says it. */
export function StudioKeyCap({ children }: { readonly children: ReactNode }) {
  return <kbd className={styles.cap}>{children}</kbd>;
}

/** „Formularz działa w MoveBuddy" — required on every public form (R11). */
export function StudioPoweredBy({ label, className }: { readonly label: string; readonly className?: string }) {
  return (
    <span className={clsx(styles.poweredBy, className)}>
      <span className={clsx(brandMark.brandMark, styles.mark)} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

/** The film grain over the whole page, drawn once per surface. */
export function StudioGrain({ className }: { readonly className?: string }) {
  const filterId = useId();
  return (
    <svg className={clsx(styles.grain, className)} aria-hidden="true" focusable="false">
      <filter id={filterId}>
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves={2} stitchTiles="stitch" />
      </filter>
      <rect width="100%" height="100%" filter={`url(#${filterId})`} />
    </svg>
  );
}
